import React, { useState, useEffect } from "react";
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  Button,
  Grid,
  makeStyles,
  LinearProgress,
  CircularProgress,
  IconButton
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useSnackbar } from "contexts/SnackbarContext";
import { paginateDocWorkspace, getDocPaginationProgress } from "./FileApi";

const useStyles = makeStyles((theme) => ({
  paginateBtn: {
    background: "white",
    border: "1px solid #E0E0E0",
    boxShadow: "none",
    width: "10.5rem",
    textTransform: "none",
    [theme.breakpoints.down("sm")]: {
      width: "8.375rem",
    },
    [theme.breakpoints.down("xs")]: {
      width: "7.125rem",
    },
  },
  paginateBtnText: {
    fontSize: "0.9375rem",
    fontWeight: "500",
    textTransform: "none",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  paper: {
    width: "450px",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4, 4, 3),
    outline: "none",
    borderRadius: "0.4rem",
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    boxShadow: theme.shadows[5],
    [theme.breakpoints.down("xs")]: {
      width: "90%",
      padding: theme.spacing(3, 2, 2),
    },
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: "700",
    marginBottom: "1rem",
  },
  bodyText: {
    fontSize: "0.9375rem",
    fontWeight: "400",
    lineHeight: "1.6",
  },
  progressWrapper: {
    marginTop: "1.5rem",
    marginBottom: "1.5rem",
  },
  progressText: {
    fontSize: "0.875rem",
    marginTop: "0.5rem",
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "500",
  },
  cancel: {
    fontSize: "1rem",
    fontWeight: "500",
    marginRight: "2rem",
    textTransform: "none",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  confirmBtn: {
    textTransform: "none",
    padding: "0.5rem 1.5rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  closeBtn: {
    position: "absolute",
    right: "0.5rem",
    top: "0.5rem",
  },
  spinner: {
    marginRight: "8px",
    color: theme.palette.primary.main,
  }
}));

export default function PaginateWorkspace({
  workspaceId,
  firstLevelFolderId,
  reload,
  paginationStatus,
  setPaginationStatus,
  paginationType = 4,
  isEmpty,
  ocrStatus
}) {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paginatedCount, setPaginatedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const inProgress = paginationStatus === 1;
  const isPaginated = paginationStatus === 2;

  const buttonText =
    paginationStatus === 3 ? 'Re-paginate' :
      paginationStatus === 2 ? 'Paginated' :
        paginationStatus === 1 ? 'Paginating...' :
          'Paginate';

  const isBtnDisabled =
    !workspaceId ||
    isPaginated ||
    isEmpty ||
    ocrStatus === "OCR in progress";

  // Handle progress polling when pagination is in progress
  useEffect(() => {
    let intervalId;
    if (inProgress && workspaceId) {
      const pollProgress = () => {
        getDocPaginationProgress(workspaceId, firstLevelFolderId)
          .then((res) => {
            const { number_of_pages_paginated, total_number_of_files } = res.data;
            const currentPaginated = number_of_pages_paginated || 0;
            const currentTotal = total_number_of_files || 0;

            setPaginatedCount(currentPaginated);
            setTotalCount(currentTotal);

            // Finish check: if progress is complete
            if (currentTotal > 0 && currentPaginated >= currentTotal) {
              setPaginationStatus(2);
              showSnackbar("Pagination completed successfully!", "success");
              if (reload) reload();
              setIsOpen(false);
            }
          })
          .catch((err) => {
            console.error("Error polling pagination progress:", err);
          });
      };

      pollProgress();
      intervalId = setInterval(pollProgress, 2500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [inProgress, workspaceId, firstLevelFolderId, reload, showSnackbar, setPaginationStatus]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    // Users can close the modal even if in progress; it runs in the background.
    setIsOpen(false);
  };

  const handleConfirm = () => {
    if (!workspaceId) {
      showSnackbar("Workspace ID not found.", "error");
      return;
    }
    setLoading(true);
    paginateDocWorkspace(workspaceId, firstLevelFolderId, paginationType)
      .then((res) => {
        setLoading(false);
        setPaginationStatus(1);
        showSnackbar("Pagination started. Processing documents...", "info");
      })
      .catch((err) => {
        setLoading(false);
        const errMsg = err.response?.data?.error || err.response?.data?.message || "Failed to start pagination.";
        showSnackbar(errMsg, "error");
        if (errMsg.toLowerCase().includes("stamped") || errMsg.toLowerCase().includes("complete")) {
          setPaginationStatus(2);
          if (reload) reload();
          setIsOpen(false);
        }
      });
  };

  const progressPercent = totalCount > 0 ? Math.min(100, Math.round((paginatedCount / totalCount) * 100)) : 0;

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpen}
        className={classes.paginateBtn}
        disabled={isBtnDisabled}
      >
        <Typography className={classes.paginateBtnText}>
          {buttonText}
        </Typography>
      </Button>

      <Modal
        open={isOpen}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={isOpen}>
          <Box className={classes.paper}>
            <IconButton className={classes.closeBtn} onClick={handleClose}>
              <Close />
            </IconButton>

            <Typography className={classes.title}>
              Paginate Documents
            </Typography>

            {!inProgress ? (
              <>
                <Typography className={classes.bodyText}>
                  Are you sure you want to paginate all the documents in this workspace? This will add page numbers to files.
                </Typography>
                <Box height="2rem" />
                <Grid container justify="flex-end">
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      className={classes.cancel}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.confirmBtn}
                      onClick={handleConfirm}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={16} className={classes.spinner} />
                          Starting...
                        </>
                      ) : (
                        "Confirm"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Typography className={classes.bodyText}>
                  Pagination is currently in progress. You can safely close this modal, and pagination will continue in the background.
                </Typography>
                <div className={classes.progressWrapper}>
                  <LinearProgress variant="determinate" value={progressPercent} />
                  <Typography className={classes.progressText}>
                    <span>{progressPercent}% completed</span>
                    <span>{paginatedCount} / {totalCount} files</span>
                  </Typography>
                </div>
                <Grid container justify="flex-end">
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.confirmBtn}
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
